/**
 * The realized equity curve. uPlot draws it; `lib/equity.ts` shapes it.
 *
 * This component adds NOTHING to the data. It receives typed arrays from a
 * pure transform and hands them to uPlot with `spanGaps: false`, so:
 *
 *   * a DECLARED GAP is a break, never a straight line drawn through it;
 *   * the MARK overlay (equity incl. unrealized at 10–20×) is a separate,
 *     dashed, OFF-BY-DEFAULT series and is never joined to the realized line;
 *   * a measured reading taken while an order was open is a dimmer points-only
 *     lane — kept, never dropped (C1);
 *   * the bounded-unknown band is drawn from the server's own aggregate at
 *     the tail of the realized line, and is labelled with its basis.
 *
 * There is no client-side smoothing, no resampling and no fill-forward.
 *
 * It is drawn as an ILLUSTRATION, at 180 px, BELOW the figures. The figures
 * are the substance. Everything a canvas can carry that CSS cannot reach —
 * the axis face, the axis colour, the tick density, the date format, the
 * money unit — is set here, once, and nowhere else.
 *
 * `PnlBars` lives in this file too, and deliberately: it is the second view of
 * the same money, and the two must never be able to disagree about what the
 * server sent.
 */

import { useEffect, useMemo, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import type { EquityPayload, OrderRow } from "../lib/api";
import { toPlotData } from "../lib/equity";
import { money } from "../lib/money";
import { stamp } from "../lib/time";

/** Canvas can't read a custom property, so the ramp is duplicated here — and
 *  ONLY here. Every value is a token from `styles/tokens.css`. */
const N300 = "#C7CFD7"; // --n-300       flow markers
const N500 = "#8A98A6"; // --n-500       axis text, band caps, measured lane
const FALCON = "#5B6C7D"; // --falcon-grey  graphic-only: the MARK overlay
const ACCENT = "#7FA6C4"; // --accent      chrome: the realized line. Chrome,
//                          not datum — it says "this is the primary series",
//                          never "this number is good". Amber stays reserved
//                          for money at risk; up/down stay reserved for signs.
const GRID = "#1a2732"; // --line-soft   horizontal rules, under the data

// 12 px is the floor for every rendered glyph in this product, canvas
// included — a canvas label is not exempt from the type scale.
const AXIS_FONT = "500 12px 'JetBrains Mono', ui-monospace, monospace";
const FLOW_FONT = "500 12px 'JetBrains Mono', ui-monospace, monospace";
const CHART_H = 180;

/** The y axis carries its unit. `8,000` alone is not a quantity. */
const USD = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const usd = (v: number) => `$${USD.format(v)}`;

/** `MM-DD` — the console speaks ISO everywhere else; the chart does not get
 *  to speak US `M/D`. */
const mmdd = (unixSeconds: number) => new Date(unixSeconds * 1000).toISOString().slice(5, 10);

export function EquityChart({ payload, showMark }: { payload: EquityPayload | undefined; showMark: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  // ONE transform per payload, shared by the plot and the figures beneath
  // it, so the two can never disagree about what the server sent.
  const d = useMemo(() => toPlotData(payload), [payload]);

  // A canvas is invisible to a screen reader. The label states the series,
  // the range and the extent, all read off the same transform the plot uses.
  const aria = useMemo(() => {
    if (d.xs.length === 0) return "realized equity curve — no readings in range";
    let lo = Number.POSITIVE_INFINITY;
    let hi = Number.NEGATIVE_INFINITY;
    let n = 0;
    for (const v of d.realized) {
      if (v === null) continue;
      n += 1;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    const t0 = new Date(d.xs[0]! * 1000).toISOString().slice(0, 10);
    const t1 = new Date(d.xs[d.xs.length - 1]! * 1000).toISOString().slice(0, 10);
    const extent = n > 0 ? `, ranging ${usd(lo)} to ${usd(hi)}` : ", no realized readings";
    return `realized equity curve${showMark ? ", with the MARK overlay shown" : ""}: ${n} realized readings from ${t0} to ${t1} UTC${extent}`;
  }, [d, showMark]);

  useEffect(() => {
    const host = hostRef.current;
    if (!payload || d.xs.length === 0 || !host) return;
    // No 2D canvas (a hardened browser, a headless DOM) — refuse to draw
    // rather than throw behind the numbers. The figures below the chart are
    // the substance; the curve is the illustration.
    if (!host.ownerDocument.createElement("canvas").getContext?.("2d")) return;

    const bandPlugin: uPlot.Plugin = {
      hooks: {
        draw: (u) => {
          // The band is an AGGREGATE the server computed over unsettled
          // closes. It is drawn once, at the tail of the realized line, and
          // never distributed across points the client cannot attribute.
          if (!d.band || d.band.n_unsettled === 0) return;
          let lastI = -1;
          for (let i = d.realized.length - 1; i >= 0; i -= 1) {
            if (d.realized[i] !== null) {
              lastI = i;
              break;
            }
          }
          if (lastI < 0) return;
          const base = d.realized[lastI]!;
          const ctx = u.ctx;
          const x0 = u.valToPos(d.xs[lastI]!, "x", true);
          const x1 = u.bbox.left + u.bbox.width;
          const w = Math.max(2, x1 - x0);
          const yLow = u.valToPos(base + d.band.low, "y", true);
          const yHigh = u.valToPos(base + d.band.high, "y", true);
          // A $5.63 band on a $5,500 scale is a sub-pixel rectangle. It is
          // drawn at a 6 px floor so it still RESOLVES — and when that floor
          // bites, the host says so rather than implying the band is wide.
          const trueH = Math.abs(yHigh - yLow);
          const h = Math.max(6, trueH);
          const clamped = h > trueH;
          const top = (yLow + yHigh) / 2 - h / 2;
          ctx.save();
          ctx.fillStyle = "rgba(91,108,125,0.20)";
          ctx.fillRect(x0, top, w, h);
          // 1 px caps: without them a floored band reads as a fill artefact.
          ctx.strokeStyle = N500;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x0, Math.round(top) + 0.5);
          ctx.lineTo(x0 + w, Math.round(top) + 0.5);
          ctx.moveTo(x0, Math.round(top + h) - 0.5);
          ctx.lineTo(x0 + w, Math.round(top + h) - 0.5);
          ctx.stroke();
          ctx.restore();
          host.title = clamped
            ? `bounded-unknown band [${money(d.band.low)} … ${money(d.band.high)}] — drawn at a 6 px floor because its true extent is thinner than one drawable band at this scale`
            : `bounded-unknown band [${money(d.band.low)} … ${money(d.band.high)}] over ${d.band.n_unsettled} unsettled close${d.band.n_unsettled === 1 ? "" : "s"}`;
        },
      },
    };

    const flowPlugin: uPlot.Plugin = {
      hooks: {
        draw: (u) => {
          if (d.flows.length === 0) return;
          // A capital flow is NOT a market move, so it is never drawn in the
          // up/down ramp and never drawn as a full-height rule competing with
          // the data. It is a mark on the baseline carrying its own amount.
          const label = d.flows.length <= 8;
          const ctx = u.ctx;
          const baseY = u.bbox.top + u.bbox.height;
          ctx.save();
          ctx.fillStyle = N300;
          ctx.strokeStyle = N300;
          ctx.lineWidth = 1;
          ctx.font = FLOW_FONT;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          for (const f of d.flows) {
            const x = Math.round(u.valToPos(f.x, "x", true)) + 0.5;
            ctx.beginPath();
            ctx.moveTo(x, baseY);
            ctx.lineTo(x, baseY - 12);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(x, baseY - 5);
            ctx.lineTo(x - 5, baseY);
            ctx.lineTo(x + 5, baseY);
            ctx.closePath();
            ctx.fill();
            if (label) ctx.fillText(money(f.amount), x, baseY - 14);
          }
          ctx.restore();
        },
      },
    };

    const series: uPlot.Series[] = [
      {},
      {
        label: "realized",
        stroke: ACCENT,
        width: 2,
        // THE line that must never be drawn through a declared gap.
        spanGaps: false,
        points: { show: false },
      },
      {
        label: "measured while open",
        stroke: N500,
        width: 0,
        spanGaps: false,
        points: { show: true, size: 3, stroke: N500, fill: N500 },
        paths: () => null,
      },
    ];
    const data: uPlot.AlignedData = [d.xs, d.realized, d.unrealized] as unknown as uPlot.AlignedData;

    if (showMark) {
      series.push({
        label: "MARK (incl. unrealized)",
        stroke: FALCON,
        width: 1,
        dash: [4, 3],
        spanGaps: false,
        points: { show: false },
      });
      (data as unknown as Array<Array<number | null>>).push(d.mark);
    }

    plotRef.current?.destroy();
    plotRef.current = new uPlot(
      {
        width: host.clientWidth || 640,
        height: CHART_H,
        cursor: { drag: { x: true, y: false } },
        // uPlot's stock legend renders `Time: --  realized: --` at rest, with
        // OS checkboxes. There is exactly ONE legend and it is authored below.
        legend: { show: false },
        scales: { x: { time: true } },
        axes: [
          {
            stroke: N500,
            font: AXIS_FONT,
            // The vertical rules were heavier than the data line.
            grid: { show: false },
            ticks: { stroke: GRID, size: 4 },
            // ~8 labels instead of 22 overlapping ones.
            space: 120,
            values: (_u, splits) => splits.map((s) => mmdd(s)),
          },
          {
            stroke: N500,
            font: AXIS_FONT,
            grid: { stroke: GRID, width: 1 },
            ticks: { stroke: GRID, size: 4 },
            // uPlot sizes an unspecified y-axis to its DEFAULT font metrics,
            // not to AXIS_FONT — the type-scale raise took the ticks to 12px
            // and "$10,000" clipped its leading digit inside the default
            // 50px gutter (audit minor, the same coupling class as the
            // row-height/estimateSize pair, in canvas). Size it to the
            // widest rendered label with the same padding uPlot would add.
            size: 68,
            values: (_u, splits) => splits.map((v) => usd(v)),
          },
        ],
        series,
        plugins: [bandPlugin, flowPlugin],
      },
      data,
      host,
    );

    const ro = new ResizeObserver(() => {
      if (plotRef.current && host.clientWidth) plotRef.current.setSize({ width: host.clientWidth, height: CHART_H });
    });
    ro.observe(host);
    return () => {
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [payload, d, showMark]);

  if (!payload) return <div className="stub">…</div>;
  return (
    <div className="equity">
      <div ref={hostRef} className="equity__host" role="img" aria-label={aria} />
      {/* ONE legend, drawn with real swatches. The box-drawing approximations
          it replaces (─ ▪ ░ ┄) are in none of the three brand faces. */}
      <div className="equity__legend">
        <span className="equity__key">
          <i className="equity__swatch equity__swatch--realized" aria-hidden="true" />
          realized
        </span>
        <span className="equity__key" title="a balance reading taken while an order was open — kept, never dropped">
          <i className="equity__swatch equity__swatch--measured" aria-hidden="true" />
          unrealized
        </span>
        <span className="equity__key" title={d.band.basis}>
          <i className="equity__swatch equity__swatch--band" aria-hidden="true" />
          SL/TP band
        </span>
        {d.flows.length > 0 && (
          <span className="equity__key">
            <i className="equity__swatch equity__swatch--flow" aria-hidden="true" />
            deposit / withdrawal
          </span>
        )}
        {showMark && (
          <span
            className="equity__key"
            title="equity including unrealized on a full-balance position at 10–20×, never joined to the realized line"
          >
            <i className="equity__swatch equity__swatch--mark" aria-hidden="true" />
            MARK
          </span>
        )}
      </div>

      {d.rejectedNonFinite > 0 && (
        <span className="chip chip--down" title="non-finite readings are rejected at ingest, never coerced to zero">
          {d.rejectedNonFinite} non-finite reading{d.rejectedNonFinite === 1 ? "" : "s"} rejected
        </span>
      )}

      {/* The three facts under the curve are CHIPS, not paragraphs. Each one
          keeps its sentence on its title. */}
      <div className="row-actions">
        {payload.gaps.length > 0 && (
          <span
            className="chip"
            title={`declared, never interpolated · ${payload.gaps.map((g) => `${Math.round(g.seconds / 60)}m`).join(" · ")}`}
          >
            {payload.gaps.length} gap{payload.gaps.length === 1 ? "" : "s"}
          </span>
        )}

        {payload.unterminated_spans.length > 0 && (
          <span className="chip chip--down" title="past the clamp this is a fault">
            {payload.unterminated_spans.length} unterminated open{payload.unterminated_spans.length === 1 ? "" : "s"} ·
            clamped
          </span>
        )}

        {d.band.n_unsettled > 0 && (
          <span className="chip mono" title={d.band.basis}>
            band [{money(d.band.low)} … {money(d.band.high)}] over {d.band.n_unsettled} close
            {d.band.n_unsettled === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ per-trade P&L */

/** The DISTRIBUTION the curve cannot show: one bar per closed trade in the
 *  bracket, oldest → newest. This is how a reader sees "three big losses,
 *  everything else flat" without reading nineteen rows.
 *
 *  It adds nothing to the data either. Bars come from the SAME `/ledger/trades`
 *  rows the list below is already showing — never a second endpoint, never a
 *  number recomputed from a page — and a trade with no P&L has no bar, because
 *  a missing P&L is not a zero.
 *
 *  Sign is carried by the up/down ramp, which is DATA-ONLY colour. Height is
 *  |pnl_pct| against the largest magnitude in the set, so the scale is stated
 *  rather than implied. */
export function PnlBars({ rows }: { rows: OrderRow[] }) {
  const bars = useMemo(() => {
    const closed = rows.filter((r) => r.close_ts !== null && r.pnl_pct !== null && Number.isFinite(r.pnl_pct));
    // The list may arrive newest-first or P&L-sorted; the strip is CHRONOLOGICAL
    // by construction, because "everything else flat" is a statement about time.
    closed.sort((a, b) => Date.parse(a.close_ts!) - Date.parse(b.close_ts!));
    return closed;
  }, [rows]);

  if (bars.length === 0) return <div className="stub">no closed trades in this range</div>;

  const peak = bars.reduce((mx, r) => Math.max(mx, Math.abs(r.pnl_pct!)), 0) || 1;

  return (
    <>
      <div className="pnlbars" role="img" aria-label={`per-trade P&L, oldest to newest, ${bars.length} closed trades`}>
        {bars.map((r) => (
          <i
            key={r.trade_id}
            className={`pnlbars__bar ${r.pnl_pct! >= 0 ? "up" : "down"}`}
            style={{ height: `${Math.max(2, (Math.abs(r.pnl_pct!) / peak) * 100)}%` }}
            title={`${r.instrument ?? "—"} ${(r.direction ?? "—").toUpperCase()} · ${stamp(r.close_ts)} · ${money(r.pnl_pct, "pct")} · ${money(r.pnl_usd)}`}
          />
        ))}
      </div>
      {/* The strip draws the rows the list has FETCHED, which is a page-bound
          set — so it says how many, rather than implying it drew the bracket.
          The bracket's own closed count is the `closed` figure above. */}
      <div className="mono dim" title="one bar per closed trade in the pages loaded so far; scroll the list to load more">
        {bars.length} drawn · full height ±{peak.toFixed(2)}%
      </div>
    </>
  );
}
